import React from 'react';
import { Head, Link, useForm, usePage } from '@inertiajs/react';
import BlogLayout from '@/Layouts/BlogLayout';
import { Heart, MessageSquare, Send, Trash2 } from 'lucide-react';

export default function Show({ blog, comments = [], recentPosts = [], likesCount = 0, userHasLiked = false, canManage }) {
    const { auth } = usePage().props;
    const { delete: destroy } = useForm();
    const { post: postLike } = useForm();
    const { data: commentData, setData: setCommentData, post: postComment, processing: processingComment, reset: resetComment } = useForm({
        content: '',
    });

    const deleteBlog = () => {
        if (confirm('Are you sure you want to delete this blog post?')) {
            destroy(route('blogs.destroy', blog.id));
        }
    };

    const handleLike = () => {
        if (!auth.user) {
            window.location.href = route('login');
            return;
        }
        postLike(route('likes.toggle', blog.id), {
            preserveScroll: true
        });
    };

    const handleCommentSubmit = (e) => {
        e.preventDefault();
        postComment(route('comments.store', blog.id), {
            preserveScroll: true,
            onSuccess: () => resetComment(),
        });
    };

    const deleteComment = (commentId) => {
        if (confirm('Delete this comment?')) {
            destroy(route('comments.destroy', commentId), {
                preserveScroll: true
            });
        }
    };

    const description = blog.content.replace(/<[^>]*>?/gm, '').substring(0, 160) + '...';

    return (
        <BlogLayout
            title={blog.title}
            description={description}
            image={blog.image}
            isScrolled={true}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row gap-12">
                {/* Main Content */}
                <div className="lg:w-2/3">
                    <div className="mb-8">
                        <Link
                            href={route('blogs.index')}
                            className="text-[#0a3f26] hover:text-[#d4a575] font-semibold flex items-center transition-colors group"
                        >
                            <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                            </svg>
                            Back to Blog
                        </Link>
                    </div>

                    <article className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm mb-12">
                        {blog.image && (
                            <div className="w-full aspect-video overflow-hidden relative group">
                                <img
                                    src={blog.image}
                                    alt={blog.title}
                                    className="w-full h-full object-contain md:object-cover transition-transform duration-700 hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none"></div>
                            </div>
                        )}

                        <div className="p-8 md:p-12">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                                <div className="text-[#d4a575] font-semibold tracking-widest uppercase text-sm">
                                    {new Date(blog.scheduled_at || blog.created_at).toLocaleDateString(undefined, {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </div>

                                <div className="flex items-center gap-6">
                                    <button
                                        onClick={handleLike}
                                        className={`flex items-center gap-2 transition-colors ${userHasLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}
                                    >
                                        <Heart size={20} fill={userHasLiked ? 'currentColor' : 'none'} />
                                        <span className="font-semibold">{likesCount}</span>
                                    </button>

                                    <div className="flex items-center gap-2 text-gray-400">
                                        <MessageSquare size={20} />
                                        <span className="font-semibold">{comments.length}</span>
                                    </div>

                                    {canManage && (
                                        <div className="flex gap-4 ml-4 pl-4 border-l border-gray-100">
                                            <Link
                                                href={route('blogs.edit', blog.id)}
                                                className="text-[#0a3f26] hover:text-[#d4a575] font-semibold text-sm transition-all"
                                            >
                                                Edit
                                            </Link>
                                            <button
                                                onClick={deleteBlog}
                                                className="text-red-600 hover:text-red-800 font-semibold text-sm transition-all"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-medium text-[#0a3f26] mb-10 leading-tight">
                                {blog.title}
                            </h1>

                            <div
                                className="prose prose-lg max-w-none 
                                prose-headings:font-display prose-headings:text-[#0a3f26] 
                                prose-p:text-gray-700 prose-p:leading-relaxed 
                                prose-img:rounded-[2rem] prose-img:shadow-xl prose-img:object-contain md:prose-img:object-cover prose-img:w-full
                                prose-a:text-[#d4a575] prose-a:no-underline hover:prose-a:underline 
                                prose-blockquote:border-[#d4a575] prose-blockquote:bg-gray-50/50 prose-blockquote:py-1 prose-blockquote:px-6 prose-blockquote:rounded-r-2xl"
                                dangerouslySetInnerHTML={{ __html: blog.content }}
                            />
                        </div>
                    </article>

                    {/* Comments Section */}
                    <section className="bg-gray-50/50 rounded-3xl p-8 md:p-12 border border-gray-100">
                        <h2 className="text-2xl font-display font-medium text-[#0a3f26] mb-8 flex items-center gap-3">
                            Comments <span className="text-sm font-sans text-gray-400 font-normal">({comments.length})</span>
                        </h2>

                        {auth.user ? (
                            <form onSubmit={handleCommentSubmit} className="mb-12">
                                <div className="relative">
                                    <textarea
                                        value={commentData.content}
                                        onChange={e => setCommentData('content', e.target.value)}
                                        placeholder="Add to the conversation..."
                                        className="w-full rounded-2xl border-gray-200 focus:border-[#d4a575] focus:ring focus:ring-[#d4a575]/20 p-4 pt-4 pb-12 transition-all resize-none min-h-[120px]"
                                        required
                                    ></textarea>
                                    <div className="absolute bottom-3 right-3">
                                        <button
                                            type="submit"
                                            disabled={processingComment || !commentData.content.trim()}
                                            className="bg-[#0a3f26] text-[#d4a575] p-3 rounded-xl hover:bg-[#062416] transition-all disabled:opacity-50 disabled:grayscale flex items-center gap-2"
                                        >
                                            <Send size={18} />
                                        </button>
                                    </div>
                                </div>
                            </form>
                        ) : (
                            <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-6 text-center mb-12">
                                <p className="text-gray-500 mb-4">Please log in to join the discussion.</p>
                                <Link
                                    href={route('login')}
                                    className="inline-block bg-[#0a3f26] text-[#d4a575] px-6 py-2 rounded-full font-semibold hover:bg-[#062416] transition-all"
                                >
                                    Login to Comment
                                </Link>
                            </div>
                        )}

                        <div className="space-y-6">
                            {comments.length > 0 ? (
                                comments.map(comment => (
                                    <div key={comment.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-50 flex gap-4">
                                        <div className="w-10 h-10 rounded-full bg-[#d4a575]/10 flex items-center justify-center text-[#d4a575] font-semibold shrink-0">
                                            {comment.user.name.charAt(0)}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h4 className="font-semibold text-[#0a3f26]">{comment.user.name}</h4>
                                                    <span className="text-xs text-gray-400">
                                                        {new Date(comment.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {(auth.user && (auth.user.id === comment.user_id || auth.user.role === 'admin')) && (
                                                    <button
                                                        onClick={() => deleteComment(comment.id)}
                                                        className="text-gray-300 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-gray-700 leading-relaxed">{comment.content}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-gray-400 italic py-8">No comments yet. Be the first to start the conversation!</p>
                            )}
                        </div>
                    </section>
                </div>

                {/* Sidebar */}
                <aside className="lg:w-1/3 space-y-8">
                    {/* Recent Posts Widget */}
                    <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm sticky top-32">
                        <h3 className="text-xl font-display font-medium text-[#0a3f26] mb-6 pb-4 border-b border-gray-100">
                            Recent Stories
                        </h3>
                        <div className="space-y-6">
                            {recentPosts && recentPosts.length > 0 ? (
                                recentPosts.map(post => (
                                    <Link
                                        key={post.id}
                                        href={route('blogs.show', post.slug)}
                                        className="group flex gap-4 items-start"
                                    >
                                        <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-gray-100">
                                            {post.image ? (
                                                <img
                                                    src={post.image}
                                                    alt={post.title}
                                                    className="w-full h-full object-contain md:object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-[#0a3f26]/20 font-bold">HR</div>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="text-[#0a3f26] font-medium leading-snug mb-1 group-hover:text-[#d4a575] transition-colors line-clamp-2">
                                                {post.title}
                                            </h4>
                                            <span className="text-xs text-gray-400 uppercase tracking-wider">
                                                {new Date(post.scheduled_at || post.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </Link>
                                ))
                            ) : (
                                <p className="text-gray-400 italic text-sm">No other stories yet.</p>
                            )}
                        </div>
                    </div>
                </aside>
            </div>
        </BlogLayout>
    );
}
